// src/services/jobTypes.js
import {
  doc,
  onSnapshot,
  setDoc,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase";

export const JOB_TYPES = [
  { code: "DV",  name: "Deliver" },
  { code: "EMP", name: "EMPTY" },
  { code: "PK",  name: "PICK UP" },
  { code: "RP",  name: "Replace" },
];

const CODE_TO_NAME = JOB_TYPES.reduce((m, j) => {
  m[j.code] = j.name;
  return m;
}, {});
const NAME_TO_CODE = {
  "DELIVER": "DV",
  "EMPTY": "EMP",
  "PICK": "PK", "PICK UP": "PK", "PICKUP": "PK",
  "REPLACE": "RP",
};

export function dropLegacy(job = {}) {
  const { code, card, type, ...rest } = job;
  return rest;
}

/**
 * Normalize ONE job.
 * If assigned via DefaultAssignJobs and no explicit job type was provided,
 * we LEAVE IT BLANK (no default to Deliver).
 */
export function normalizeJobType(job = {}) {
  // If this came from the default assign flow and there is no explicit job type, keep blank.
  const fromDefault = (job.assignedSource || "").toLowerCase() === "default";
  const hasExplicit =
    !!job.jobTypeCode ||
    !!job.jobTypeName ||
    !!job.jobType ||
    !!job.code ||
    !!job.card;

  if (fromDefault && !hasExplicit) {
    return dropLegacy(job); // no auto-fill
  }

  if (job.jobTypeCode && job.jobTypeName) return dropLegacy(job);

  const rawName = String(
    job.jobTypeName || job.jobType || job.code || job.card || ""
  ).toUpperCase().trim();
  const rawCode = String(job.jobTypeCode || "").toUpperCase().trim();

  let code = rawCode;
  let name = job.jobTypeName || "";

  if (!code && rawName) {
    for (const key of Object.keys(NAME_TO_CODE)) {
      if (rawName.includes(key)) { code = NAME_TO_CODE[key]; break; }
    }
  }
  // If still no code, default to DV (non-default flows only)
  if (!code) code = "DV";
  if (!name) name = CODE_TO_NAME[code] || "Deliver";

  return dropLegacy({ ...job, jobTypeCode: code, jobTypeName: name });
}

/** Normalize the whole runs object */
export function sanitizeRuns(runs = {}) {
  const cleaned = {};
  for (const [runKey, arr] of Object.entries(runs || {})) {
    cleaned[runKey] = (arr || []).map((j) => normalizeJobType(j));
  }
  return cleaned;
}

export function assignmentDocIds({ companyId, driverId, date }) {
  const legacyId = `${driverId}_${date}`;
  const scopedId = companyId ? `${companyId}_${driverId}_${date}` : null;
  return { legacyId, scopedId };
}

export async function saveJobTypes({ companyId, driverId, date, runs, otherMerge = {} }) {
  if (!driverId || !date) return;
  const { legacyId, scopedId } = assignmentDocIds({ companyId, driverId, date });

  const payload = {
    runs: sanitizeRuns(runs || {}),
    updatedAt: Date.now(),
    ...otherMerge,
  };

  await setDoc(doc(db, "assignments", legacyId), payload, { merge: true });
  if (scopedId) {
    await setDoc(doc(db, "assignments", scopedId), payload, { merge: true });
  }
}

/**
 * Subscribe to job types with scoped->legacy fallback.
 */
export function subscribeJobTypes({ companyId, driverId, date }, cb) {
  if (!driverId || !date) return () => {};
  const { legacyId, scopedId } = assignmentDocIds({ companyId, driverId, date });

  let unsubScoped = null;
  let unsubLegacy = null;
  let usingScoped = false;

  if (scopedId) {
    unsubScoped = onSnapshot(doc(db, "assignments", scopedId), (snap) => {
      if (snap.exists()) {
        usingScoped = true;
        const d = snap.data() || {};
        cb({ runs: sanitizeRuns(d.runs || {}), source: "scoped" });
      } else if (!usingScoped && !unsubLegacy) {
        unsubLegacy = onSnapshot(doc(db, "assignments", legacyId), (lsnap) => {
          const d = lsnap.exists() ? (lsnap.data() || {}) : {};
          cb({ runs: sanitizeRuns(d.runs || {}), source: "legacy" });
        });
      }
    });
  } else {
    unsubLegacy = onSnapshot(doc(db, "assignments", legacyId), (lsnap) => {
      const d = lsnap.exists() ? (lsnap.data() || {}) : {};
      cb({ runs: sanitizeRuns(d.runs || {}), source: "legacy" });
    });
  }

  return () => {
    if (unsubScoped) unsubScoped();
    if (unsubLegacy) unsubLegacy();
  };
}

/**
 * UI mapping. When jobType is missing => neutral placeholder.
 */
export function jobTypeUi(jobTypeCode) {
  if (!jobTypeCode) return { label: "—", intent: "neutral", card: null };
  const code = (jobTypeCode || "").toUpperCase();
  switch (code) {
    case "EMP": return { label: "EMPTY",   intent: "warning", card: "empty"   };
    case "PK":  return { label: "PICK UP", intent: "neutral", card: "pickup"  };
    case "RP":  return { label: "REPLACE", intent: "accent",  card: "deliver" };
    case "DV":  return { label: "DELIVER", intent: "primary", card: "deliver" };
    default:    return { label: code,      intent: "neutral", card: null };
  }
}

export function cardFromCode(jobTypeCode) {
  const { card } = jobTypeUi(jobTypeCode);
  return card; // "empty" | "pickup" | "deliver" | null
}

/**
 * Create/update job docs for every stop; omit jobType fields if blank.
 */
export async function ensureJobsForRuns({ companyId = "", driverId, date, runs }) {
  if (!driverId || !date) return sanitizeRuns(runs || {});
  const next = {};

  const assignmentLegacyId = `${driverId}_${date}`;
  const assignmentScopedId = companyId ? `${companyId}_${driverId}_${date}` : null;

  for (const [runKey, arr] of Object.entries(runs || {})) {
    next[runKey] = await Promise.all(
      (arr || []).map(async (raw, index) => {
        const base = normalizeJobType(raw); // may keep blank if default-assign + no type
        const jobPayload = {
          companyId: companyId || "",
          driverId,
          date,
          runKey,
          index,
          assignmentLegacyId,
          ...(assignmentScopedId ? { assignmentScopedId } : {}),
          status: "assigned",
          assignedSource: base.assignedSource || "EditJobsTab",
          // job type (conditionally added below)
          // jobTypeCode: base.jobTypeCode,
          // jobTypeName: base.jobTypeName,
          address: base.address || "",
          customerId: base.customerId || "",
          customerName: base.customerName || "",
          siteId: base.siteId || "",
          siteLabel: base.siteLabel || "",
          siteContact: base.siteContact || "",
          sitePhone: base.sitePhone || "",
          siteEmail: base.siteEmail || "",
          description: base.description || "",
          eta: base.eta || "",
          poNumber: base.poNumber || "",
          updatedAt: serverTimestamp(),
        };

        // Only include jobType fields if defined (avoid Firestore `undefined`)
        if (base.jobTypeCode) {
          jobPayload.jobTypeCode = base.jobTypeCode;
          jobPayload.jobTypeName = base.jobTypeName || CODE_TO_NAME[base.jobTypeCode];
        }

        if (!base.jobId) {
          const ref = await addDoc(collection(db, "jobs"), {
            ...jobPayload,
            createdAt: serverTimestamp(),
          });
          return { ...base, jobId: ref.id };
        } else {
          await setDoc(doc(db, "jobs", base.jobId), jobPayload, { merge: true });
          return base;
        }
      })
    );
  }

  return next;
}
